import React, { useState, useEffect } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import SideHelpPanel from './components/SideHelpPanel';
import ProductNav from './components/ProductNav';

const BILLING_TERMS = [
  { key: 'monthly',  label: 'Monthly',   months: 1  },
  { key: '6month',   label: '6 Months',  months: 6  },
  { key: '12month',  label: '12 Months', months: 12 },
];

// -- VISINVOKE UNIFIED PLAN PRICING ------------------------------------------
// Owner price authority. Every value here is the amount the corresponding Stripe
// Price actually charges, so what a customer is shown is what they are billed.
// These superseded a stale lower display set (6.99 / 16.99 / 27.99 / 44.99 / 84.99)
// that never matched the Stripe Prices behind the checkout.
const PLAN_PRICES = {
  artisan:     { monthly: '9.99',  '6month': '59',  '12month': '119'  },
  architect:   { monthly: '25',    '6month': '150', '12month': '300'  },
  visionary:   { monthly: '40',    '6month': '240', '12month': '480'  },
  creator_pro: { monthly: '65',    '6month': '390', '12month': '780'  },
  commercial:  { monthly: '130',   '6month': '780', '12month': '1560' },
};

// -- PROFESSIONAL GENERATOR PLANS --------------------------------------------
// Prices, spec allowances and copy come from the Professional pricing authority
// (upgrade-temp.txt PRO_PLAN_PRICES / PRO_PLAN_DETAILS), which agrees exactly with
// backend/add_pro_and_battlemap_plans.py and with the Stripe Prices.
const PRO_PLAN_PRICES = {
  pro_starter:    { monthly: '49',  '6month': '294',  '12month': '588'   },
  pro_creator:    { monthly: '119', '6month': '714',  '12month': '1428'  },
  pro_studio:     { monthly: '229', '6month': '1374', '12month': '2748'  },
  pro_agency:     { monthly: '399', '6month': '2394', '12month': '4788'  },
  pro_enterprise: { monthly: '899', '6month': '5394', '12month': '10788' },
};

const PRO_PLANS = [
  { name: 'Pro Starter',    slug: 'pro_starter',    color: '#10b981', specs: 100,
    desc: 'Refined structured system for consistent output. Optimised configuration paths for faster generation.' },
  { name: 'Pro Creator',    slug: 'pro_creator',    color: '#8b5cf6', specs: 250,
    desc: 'Designed for repeatable, reliable results. Reduced noise, increased precision for ongoing projects.' },
  { name: 'Pro Studio',     slug: 'pro_studio',     color: '#f97316', specs: 500,
    desc: 'Built for production workflows and ongoing projects. Structured for speed and consistency at scale.' },
  { name: 'Pro Agency',     slug: 'pro_agency',     color: '#0ea5e9', specs: 900,
    desc: 'Optimised for repeatable output across multiple projects. Designed for teams and production environments.' },
  { name: 'Pro Enterprise', slug: 'pro_enterprise', color: '#dc2626', specs: 2000,
    desc: 'Maximum capacity for enterprise-scale production workflows. Full commercial rights on all outputs.' },
];

// -- BATTLEMAPS SUBSCRIPTIONS -------------------------------------------------
const BATTLEMAP_SUB_PRICES = {
  campaign:      { monthly: '65',  '6month': '390',  '12month': '780'  },
  dm_pro:        { monthly: '149', '6month': '894',  '12month': '1788' },
  world_builder: { monthly: '299', '6month': '1794', '12month': '3588' },
  master_gm:     { monthly: '499', '6month': '2994', '12month': '5988' },
};

const BATTLEMAP_SUBS = [
  { name: 'Campaign',      slug: 'campaign',      color: '#10b981', specs: 150,
    desc: 'Tactical maps, assets, and environment generation for regular campaign use.' },
  { name: 'DM Pro',        slug: 'dm_pro',        color: '#8b5cf6', specs: 350,
    desc: 'Grid-aligned, VTT-ready outputs for professional dungeon masters running regular sessions.' },
  { name: 'World Builder', slug: 'world_builder', color: '#f97316', specs: 700,
    desc: 'Scalable map generation workflows designed for rapid encounter deployment at scale.' },
  { name: 'Master GM',     slug: 'master_gm',     color: '#dc2626', specs: 1200,
    desc: 'Maximum capacity for high-volume map creation. Designed for serious campaign management.' },
];

// -- PROFESSIONAL CREDIT PACKS (one-time) ------------------------------------
const PRO_CREDIT_PACKS = [
  { slug: 'starter_credits',    name: 'Starter Credits',    specs: 100,  price: '49.00',
    desc: 'Entry-level one-time credit bundle for short production runs.' },
  { slug: 'project_credits',    name: 'Project Credits',    specs: 250,  price: '99.00',
    desc: 'Ideal for a defined project scope. No subscription required.' },
  { slug: 'production_credits', name: 'Production Credits', specs: 500,  price: '179.00',
    desc: 'Suited for a full production cycle or larger campaign.' },
  { slug: 'bulk_credits',       name: 'Bulk Credits',       specs: 1000, price: '329.00',
    desc: 'Maximum one-time volume. Best value per image for committed buyers.' },
];

// Image estimate ranges per credit count (draft=1cr, refined=2cr, final=4cr)
// Rough guide: 1 credit ~ 0.5-1 image at mixed quality usage
const IMAGE_ESTIMATES = {
  10:   '5-10',
  80:   '40-80',
  220:  '110-220',
  420:  '210-420',
  700:  '350-700',
  1100: '550-1,100',
};

const PLAN_DETAILS = {
  'Explorer': {
    slug: 'explorer',
    color: '#6b7280',
    desc: 'Try VISINVOKE free - no credit card required. Access all systems and create up to 10 credits.',
    highlight: false,
    benefit: null,
    specDiff: null,
    specs: 10,
    isFree: true,
  },
  'Artisan': {
    slug: 'artisan',
    color: '#10b981',
    desc: 'Perfect for hobbyists and personal projects. Full access to all VISINVOKE systems.',
    highlight: false,
    benefit: null,
    specDiff: null,
    specs: 80,
    isFree: false,
  },
  'Architect': {
    slug: 'architect',
    color: '#8b5cf6',
    desc: 'Most popular - serious creators and regular users. Balanced volume for consistent production.',
    highlight: true,
    benefit: 'Best for regular creators',
    specDiff: '+140 vs Artisan',
    specs: 220,
    isFree: false,
  },
  'Visionary': {
    slug: 'visionary',
    color: '#f97316',
    desc: 'High-volume and professional use. Ideal for studios producing at scale each month.',
    highlight: false,
    benefit: 'Ideal for heavy creators and campaigns',
    specDiff: '+200 vs Architect',
    specs: 420,
    isFree: false,
  },
  'Creator Pro': {
    slug: 'creator_pro',
    color: '#0ea5e9',
    desc: 'Advanced creators stepping up to professional volume. Great for consistent high-output campaigns.',
    highlight: false,
    benefit: 'Perfect for high-volume creators',
    specDiff: '+280 vs Visionary',
    specs: 700,
    isFree: false,
  },
  'Commercial': {
    slug: 'commercial',
    color: '#dc2626',
    desc: 'Full commercial rights for business use. Enterprise-scale volume with commercial licensing included.',
    highlight: false,
    benefit: 'Designed for studios and professional use',
    specDiff: '+400 vs Creator Pro',
    specs: 1100,
    isFree: false,
  },
};

const PLAN_ORDER = ['Explorer', 'Artisan', 'Architect', 'Visionary', 'Creator Pro', 'Commercial'];

const EVERYTHING_INCLUDED = [
  { icon: '\u{1F409}', label: 'D&D Image Generators' },
  { icon: '\u{1F4BC}', label: 'Professional Generators' },
  { icon: '\u{1F5FA}\uFE0F', label: 'Battlemaps System' },
  { icon: '\u2694\uFE0F', label: 'BattleTracker' },
];

// -- CONTENT PACKS ------------------------------------------------------------
// Prices are the amounts the Stripe Prices behind /api/payments/create-checkout
// actually charge, and match routes/hobby_packs.py HOBBY_PACK_TIERS. Names, slugs,
// order and entitlements are unchanged; only the stale display prices were corrected
// (they previously read 9.99 / 16.99 / 29.99 / 59.99).
const CONTENT_PACKS = [
  { slug: 'starter',  name: 'Starter',  price: '15', desc: 'Unlocks additional environments, assets, and generation options.' },
  { slug: 'explorer', name: 'Explorer', price: '25', desc: 'Unlocks additional environments, assets, and generation options.' },
  { slug: 'creator',  name: 'Creator',  price: '45', desc: 'Unlocks additional environments, assets, and generation options.' },
  { slug: 'ultimate', name: 'Ultimate', price: '89', desc: 'Unlocks additional environments, assets, and generation options.' },
];

export default function Upgrade() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const [billingTerm, setBillingTerm]         = useState('12month');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [packLoading, setPackLoading]         = useState(null);
  const [error,      setError]                = useState('');
  const [packError,  setPackError]            = useState('');
  const navigate = useNavigate();
  useAuth();

  useEffect(() => {
    if (window.location.hash === '#packs') {
      setTimeout(() => {
        const el = document.getElementById('packs');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, []);

  const handleUpgrade = async (planSlug, term) => {
    if (planSlug === 'explorer') { navigate('/dashboard'); return; }
    const key = `${planSlug}_${term}`;
    setCheckoutLoading(key);
    setError('');
    try {
      const r = await consentCheckout.start(() => API.post('/v1/subscriptions/checkout', { plan_slug: planSlug, billing_term: term }), { purchaseType: 'subscription', intendedSelector: planSlug });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed - no credits were used. Try again or choose another payment method.');
      setCheckoutLoading(null);
    }
  };

  const handlePackPurchase = async (slug) => {
    setPackLoading(slug);
    setPackError('');
    try {
      const r = await consentCheckout.start(() => API.post('/api/payments/create-checkout', { pack_key: `${slug}_pack` }), { purchaseType: 'pack', intendedSelector: `${slug}_pack` });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setPackError(err.response?.data?.error || 'Payment failed - no credits were used. Try again or choose another payment method.');
      setPackLoading(null);
    }
  };

  const isMonthly = billingTerm === 'monthly';

  return (
    <div style={s.page}>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      <SideHelpPanel title="VISINVOKE Plans">
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
          <div>
            <span style={hpu.heading}>What This Page Is</span>
            <p style={hpu.body}>This page allows you to select a VISINVOKE subscription plan. All plans include access to every system - D&D Generators, Professional Generators, Battlemaps, and BattleTracker.</p>
          </div>
          <div style={hpu.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpu.heading}>Credits</span>
            <p style={hpu.body}>Credits are consumed when you generate images. Draft uses fewer credits than Refined or Final quality. Battlemaps use more credits than standard generators.</p>
          </div>
          <div style={hpu.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpu.heading}>Billing Options</span>
            <p style={hpu.body}>All subscriptions support Monthly, 6 Month, and 12 Month billing. The 12 Month option is selected by default.</p>
          </div>
          <div style={hpu.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpu.heading}>Content Packs</span>
            <p style={hpu.body}>One-time purchases that unlock additional environments, assets, and generation options. No subscription required.</p>
          </div>
          <div style={hpu.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpu.heading}>Additional Credits</span>
            <p style={hpu.body}>Purchase additional credits if you need more generations beyond your monthly allocation. One per billing cycle.</p>
          </div>
        </div>
      </SideHelpPanel>

      <ProductNav />

      <div style={s.container}>

        {/* Section 1 - Checkout header: remove hesitation */}
        <div style={s.pageHeader}>
          <h1 style={s.pageTitle}>VISINVOKE Plans</h1>
          <div style={s.priceLockBanner}>
            <div style={s.priceLockHeadline}>You're locking your price permanently</div>
            <div style={s.priceLockSub}>Your plan grows as VISINVOKE expands - at no extra cost</div>
          </div>
          <p style={s.specExplain}>One subscription. Every system included.</p>
        </div>

        {/* Everything included */}
        <div style={s.everythingCard}>
          <div style={s.everythingTitle}>Everything included</div>
          <div style={s.everythingGrid}>
            {EVERYTHING_INCLUDED.map((item, i) => (
              <div key={i} style={s.everythingItem}>
                <span style={s.everythingIcon}>{item.icon}</span>
                <span style={s.everythingLabel}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Billing term */}
        <div style={s.termSelectorWrap}>
          <div style={s.termLabel}>Billing Period</div>
          <div style={s.termSelector}>
            {BILLING_TERMS.map(t => (
              <button
                key={t.key}
                style={{ ...s.termBtn, ...(billingTerm === t.key ? s.termBtnActive : {}) }}
                onClick={() => setBillingTerm(t.key)}
              >
                {t.label}{t.key === '12month' ? ' (Best Value)' : ''}
              </button>
            ))}
          </div>
        </div>



        {error && (
          <div style={s.errorBox}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Payment failed - no credits were used</div>
            <div>{error}</div>
            <div style={{ marginTop: 6, fontSize: 12 }}>Try again or choose another payment method.</div>
          </div>
        )}

        <div style={s.grid}>
          {PLAN_ORDER.map(name => {
            const details = PLAN_DETAILS[name];
            const price   = details.isFree ? null : PLAN_PRICES[details.slug]?.[billingTerm];
            const loadKey = `${details.slug}_${billingTerm}`;
            const imgEst  = IMAGE_ESTIMATES[details.specs];
            return (
              <div key={name} style={{ ...s.card, ...(details.highlight ? s.cardHighlight : {}), borderTop: `4px solid ${details.color}` }}>
                {details.highlight && <div style={{ ...s.popularBadge, background: 'linear-gradient(135deg, #8b5cf6, #a855f7)' }}>Most Popular</div>}
                <div style={s.planName}>{name}</div>
                <div style={s.planDesc}>{details.desc}</div>
                {details.slug === 'explorer' && (
                  <div style={s.explorerNote}>10 credits total. No monthly reset. Upgrade anytime.</div>
                )}
                {details.benefit && <div style={s.planBenefit}>{details.benefit}</div>}
                <div style={s.price}>
                  {details.isFree ? (
                    <span style={s.priceFree}>Free</span>
                  ) : (
                    <>
                      <span style={s.priceCurrency}>$</span>
                      <span style={s.priceAmount}>{price}</span>
                      <span style={s.priceUsd}>USD</span>
                      {billingTerm === 'monthly' && <span style={s.pricePer}>/mo</span>}
                      {billingTerm === '6month'  && <span style={s.pricePer}> / 6 months</span>}
                      {billingTerm === '12month' && <span style={s.pricePer}> / year</span>}
                    </>
                  )}
                </div>

                {/* Section 4 - Urgency insert */}
                {!details.isFree && (
                  <div style={s.urgencyLine}>
                    Prices increase as generators grow. Your price is locked today.
                  </div>
                )}

                {/* Section 2 - Credit clarity with image estimate */}
                <div style={s.specsLine}>
                  {details.isFree
                    ? `${details.specs} credits total`
                    : `${details.specs} credits/month${imgEst ? ` (\u2248 ${imgEst} images)` : ''}`}
                </div>
                {details.specDiff && <div style={s.specDiff}>{details.specDiff}</div>}

                <div style={s.divider} />
                <div style={s.featureList}>
                  <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />D&D Image Generators</div>
                  <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />Professional Generators</div>
                  <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />Battlemaps System</div>
                  <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />BattleTracker</div>
                  {!details.isFree && <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />Monthly credit reset</div>}
                  {!details.isFree && <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />Additional credits available</div>}
                  {details.slug === 'commercial' && <div style={s.feature}><span style={{ ...s.featureDot, background: details.color }} />Commercial licence included</div>}
                </div>

                {/* Section 10 - Trust signals */}
                {!details.isFree && (
                  <div style={s.trustSignals}>
                    <span>Secure payment via Stripe</span>
                    <span style={s.trustDot}>-</span>
                    <span>No lock-in contracts</span>
                    <span style={s.trustDot}>-</span>
                    <span>Cancel anytime</span>
                  </div>
                )}

                {/* Section 11 - Button language + Section 9 - CTA */}
                <button
                  style={{
                    ...s.btn,
                    ...glassSurface(details.color, details.color, details.color),
                    opacity: checkoutLoading === loadKey ? 0.6 : 1,
                  }}
                  disabled={!!checkoutLoading}
                  onClick={() => handleUpgrade(details.slug, billingTerm)}
                >
                  {checkoutLoading === loadKey
                    ? 'Redirecting...'
                    : details.isFree
                      ? `Get ${name}`
                      : 'Lock Price Now'}
                </button>
              </div>
            );
          })}
        </div>

        <p style={s.note}>Secure payment via Stripe. Upgrades take effect immediately. Cancel anytime.</p>
        <div style={s.subscriptionValueMsg}>Subscriptions provide the best value for regular creators. Upgrade your plan or purchase additional credits at any time.</div>

        {/* Professional Generator Plans */}
        <div id="professional-plans" style={s.packsSection}>
          <div style={s.packsSectionHeader}>
            <h2 style={s.packsSectionTitle}>Professional Generator Plans</h2>
            <p style={s.packsSectionSub}>Higher-volume plans built for professional and production workflows. Billed on the same period selected above.</p>
          </div>
          <div style={s.grid}>
            {PRO_PLANS.map(plan => {
              const price   = PRO_PLAN_PRICES[plan.slug][billingTerm];
              const loadKey = `${plan.slug}_${billingTerm}`;
              return (
                <div key={plan.slug} style={{ ...s.card, borderTop: `4px solid ${plan.color}` }}>
                  <div style={s.planName}>{plan.name}</div>
                  <div style={s.planDesc}>{plan.desc}</div>
                  <div style={s.price}>
                    <span style={s.priceCurrency}>$</span>
                    <span style={s.priceAmount}>{price}</span>
                    <span style={s.priceUsd}>USD</span>
                    <span style={s.pricePer}>/{BILLING_TERMS.find(t => t.key === billingTerm).label.toLowerCase()}</span>
                  </div>
                  <div style={s.specsLine}>{plan.specs.toLocaleString()} credits per month</div>
                  <div style={s.divider} />
                  <div style={s.featureList}>
                    <div style={s.feature}><span style={{ ...s.featureDot, background: plan.color }} />Every VISINVOKE system included</div>
                    <div style={s.feature}><span style={{ ...s.featureDot, background: plan.color }} />Monthly credit reset</div>
                    <div style={s.feature}><span style={{ ...s.featureDot, background: plan.color }} />Additional credits available</div>
                  </div>
                  <div style={s.trustSignals}>
                    <span>Secure payment via Stripe</span>
                    <span style={s.trustDot}>-</span>
                    <span>No lock-in contracts</span>
                    <span style={s.trustDot}>-</span>
                    <span>Cancel anytime</span>
                  </div>
                  <button
                    style={{ ...s.btn, background: plan.color, opacity: checkoutLoading === loadKey ? 0.6 : 1 }}
                    disabled={!!checkoutLoading}
                    onClick={() => handleUpgrade(plan.slug, billingTerm)}
                  >
                    {checkoutLoading === loadKey ? 'Redirecting...' : `Choose ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>
          <p style={s.note}>Cancelling stops the next renewal. Access continues to the end of the period you have paid for.</p>
        </div>

        {/* Battlemaps Subscriptions */}
        <div id="battlemap-subscriptions" style={s.packsSection}>
          <div style={s.packsSectionHeader}>
            <h2 style={s.packsSectionTitle}>Battlemaps Subscriptions</h2>
            <p style={s.packsSectionSub}>Recurring plans for regular battlemap and encounter production. Billed on the same period selected above.</p>
          </div>
          <div style={s.grid}>
            {BATTLEMAP_SUBS.map(plan => {
              const price   = BATTLEMAP_SUB_PRICES[plan.slug][billingTerm];
              const loadKey = `${plan.slug}_${billingTerm}`;
              return (
                <div key={plan.slug} style={{ ...s.card, borderTop: `4px solid ${plan.color}` }}>
                  <div style={s.planName}>{plan.name}</div>
                  <div style={s.planDesc}>{plan.desc}</div>
                  <div style={s.price}>
                    <span style={s.priceCurrency}>$</span>
                    <span style={s.priceAmount}>{price}</span>
                    <span style={s.priceUsd}>USD</span>
                    <span style={s.pricePer}>/{BILLING_TERMS.find(t => t.key === billingTerm).label.toLowerCase()}</span>
                  </div>
                  <div style={s.specsLine}>{plan.specs.toLocaleString()} credits per month</div>
                  <div style={s.divider} />
                  <div style={s.featureList}>
                    <div style={s.feature}><span style={{ ...s.featureDot, background: plan.color }} />Every VISINVOKE system included</div>
                    <div style={s.feature}><span style={{ ...s.featureDot, background: plan.color }} />Monthly credit reset</div>
                    <div style={s.feature}><span style={{ ...s.featureDot, background: plan.color }} />Additional credits available</div>
                  </div>
                  <div style={s.trustSignals}>
                    <span>Secure payment via Stripe</span>
                    <span style={s.trustDot}>-</span>
                    <span>No lock-in contracts</span>
                    <span style={s.trustDot}>-</span>
                    <span>Cancel anytime</span>
                  </div>
                  <button
                    style={{ ...s.btn, background: plan.color, opacity: checkoutLoading === loadKey ? 0.6 : 1 }}
                    disabled={!!checkoutLoading}
                    onClick={() => handleUpgrade(plan.slug, billingTerm)}
                  >
                    {checkoutLoading === loadKey ? 'Redirecting...' : `Choose ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>
          <p style={s.note}>Cancelling stops the next renewal. Access continues to the end of the period you have paid for.</p>
        </div>

        {/* Professional Credit Packs */}
        <div id="professional-credit-packs" style={s.packsSection}>
          <div style={s.packsSectionHeader}>
            <h2 style={s.packsSectionTitle}>Professional Credit Packs</h2>
            <p style={s.packsSectionSub}>One-time credit bundles for professional production. No subscription required, and they never expire.</p>
          </div>
          {packError && <div style={s.errorBox}>{packError}</div>}
          <div style={s.packsGrid}>
            {PRO_CREDIT_PACKS.map(pack => (
              <div key={pack.slug} style={s.packCard}>
                <div style={s.packCardName}>{pack.name}</div>
                <div style={s.packCardDesc}>{pack.desc}</div>
                <div style={s.packCardPrice}>
                  <span style={s.packCurrency}>$</span>
                  <span style={s.packAmount}>{pack.price}</span>
                  <span style={s.packUsd}>USD</span>
                </div>
                <div style={s.packDivider} />
                <div style={s.packFeatureList}>
                  <div style={s.packFeature}><span style={s.packDot} />{pack.specs.toLocaleString()} credits</div>
                  <div style={s.packFeature}><span style={s.packDot} />One-time payment</div>
                  <div style={s.packFeature}><span style={s.packDot} />Never expires</div>
                </div>
                <button
                  style={{ ...s.packBtn, opacity: packLoading === pack.slug ? 0.6 : 1 }}
                  disabled={!!packLoading}
                  onClick={() => handlePackPurchase(pack.slug)}
                >
                  {packLoading === pack.slug ? 'Redirecting...' : 'Continue to Secure Checkout'}
                </button>
              </div>
            ))}
          </div>
          <p style={s.note}>Professional Credit Pack purchases are one-time payments. No subscriptions, no renewals.</p>
        </div>

        {/* Content Packs */}
        <div id="packs" style={s.packsSection}>
          <div style={s.packsSectionHeader}>
            <h2 style={s.packsSectionTitle}>Content Packs</h2>
            <p style={s.packsSectionSub}>One-time purchases. No subscription required. Unlocks additional environments, assets, and generation options.</p>
          </div>
          <div style={s.packsCompare}>
            <div style={s.packsCompareItem}><span style={s.packsCompareIcon}>&#x2713;</span><span>No ongoing commitment</span></div>
            <div style={s.packsCompareItem}><span style={s.packsCompareIcon}>&#x2713;</span><span>All VISINVOKE systems included</span></div>
            <div style={s.packsCompareItem}><span style={s.packsCompareIcon}>&#x2713;</span><span>Never expires</span></div>
            <div style={{ ...s.packsCompareItem, color: '#9ca3af' }}><span style={{ ...s.packsCompareIcon, color: '#9ca3af' }}>&#x2715;</span><span>No monthly credit reset</span></div>
            <div style={{ ...s.packsCompareItem, color: '#9ca3af' }}><span style={{ ...s.packsCompareIcon, color: '#9ca3af' }}>&#x2715;</span><span>Additional credits not available</span></div>
          </div>
          {packError && <div style={s.errorBox}>{packError}</div>}
          <div style={s.packsGrid}>
            {CONTENT_PACKS.map(pack => (
              <div key={pack.slug} style={s.packCard}>
                <div style={s.packCardName}>{pack.name}</div>
                <div style={s.packCardDesc}>{pack.desc}</div>
                <div style={s.packCardPrice}>
                  <span style={s.packCurrency}>$</span>
                  <span style={s.packAmount}>{pack.price}</span>
                  <span style={s.packUsd}>USD</span>
                </div>
                <div style={s.packDivider} />
                <div style={s.packFeatureList}>
                  <div style={s.packFeature}><span style={s.packDot} />One-time payment</div>
                  <div style={s.packFeature}><span style={s.packDot} />Never expires</div>
                  <div style={s.packFeature}><span style={s.packDot} />All VISINVOKE systems included</div>
                </div>
                <button
                  style={{ ...s.packBtn, opacity: packLoading === pack.slug ? 0.6 : 1 }}
                  disabled={!!packLoading}
                  onClick={() => handlePackPurchase(pack.slug)}
                >
                  {packLoading === pack.slug ? 'Redirecting...' : 'Continue to Secure Checkout'}
                </button>
              </div>
            ))}
          </div>
          <p style={s.note}>Content Pack purchases are one-time payments. No subscriptions, no renewals.</p>
        </div>

      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif", paddingBottom: 60 },
  container: { maxWidth: 1200, margin: '0 auto', padding: '32px 40px' },
  pageHeader: { marginBottom: 24, textAlign: 'center' },
  pageTitle: { color: '#fff', fontSize: 34, fontWeight: 800, margin: '0 0 10px' },
  priceLockBanner: { background: 'rgba(139,92,246,0.18)', border: '1px solid rgba(139,92,246,0.5)', borderRadius: 12, padding: '14px 24px', margin: '0 auto 16px', maxWidth: 560 },
  priceLockHeadline: { color: '#c4b5fd', fontSize: 17, fontWeight: 800, marginBottom: 4 },
  priceLockSub: { color: 'rgba(255,255,255,0.75)', fontSize: 13 },
  specExplain: { color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.6, maxWidth: 680, margin: '0 auto', fontStyle: 'italic' },
  everythingCard: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, padding: '20px 28px', marginBottom: 32, textAlign: 'center' },
  everythingTitle: { color: '#fff', fontWeight: 800, fontSize: 16, marginBottom: 16 },
  everythingGrid: { display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap' },
  everythingItem: { display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600 },
  everythingIcon: { fontSize: 20 },
  everythingLabel: {},
  termSelectorWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 },
  termLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600 },
  termSelector: { display: 'flex', background: 'rgba(30,16,60,0.7)', border: '1px solid rgba(139,92,246,0.45)', borderRadius: 12, padding: 4, gap: 4 },
  termBtn: { padding: '10px 22px', border: 'none', borderRadius: 8, background: 'transparent', color: 'rgba(255,255,255,0.65)', cursor: 'pointer', fontSize: 14, fontWeight: 600, transition: 'all 0.2s', letterSpacing: '0.01em' },
  termBtnActive: { background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', boxShadow: '0 2px 12px rgba(139,92,246,0.55)', fontWeight: 700 },
  yearlyNudge: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, background: 'rgba(251,191,36,0.15)', border: '2px solid #fbbf24', borderRadius: 12, padding: '14px 24px', marginBottom: 24, flexWrap: 'wrap' },
  yearlyNudgeText: { color: '#fde68a', fontWeight: 700, fontSize: 15 },
  yearlyNudgeBtn: { background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', color: '#1a1a1a', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 800, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  errorBox: { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: 10, marginBottom: 24, textAlign: 'center' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 16 },
  card: { background: '#fff', borderRadius: 16, padding: 24, position: 'relative', boxShadow: '0 4px 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 10 },
  cardHighlight: { boxShadow: '0 8px 40px rgba(139,92,246,0.35)', transform: 'scale(1.03)' },
  popularBadge: { position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap' },
  planName: { fontWeight: 800, fontSize: 20, color: '#1a1a1a' },
  planDesc: { fontSize: 13, color: '#6b7280', lineHeight: 1.5, minHeight: 52 },
  explorerNote: { fontSize: 12, color: '#10b981', fontWeight: 600, fontStyle: 'italic' },
  planBenefit: { fontSize: 12, color: '#374151', fontWeight: 600, background: '#f3f4f6', borderRadius: 6, padding: '4px 8px', display: 'inline-block' },
  price: { display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 4 },
  priceFree: { fontSize: 30, fontWeight: 800, color: '#10b981' },
  priceCurrency: { fontSize: 15, fontWeight: 700, color: '#1a1a1a' },
  priceUsd:      { fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.05em', alignSelf: 'flex-end', marginBottom: 5, marginLeft: 2 },
  priceAmount: { fontSize: 34, fontWeight: 800, color: '#1a1a1a' },
  pricePer: { fontSize: 13, color: '#9ca3af' },
  urgencyLine: { fontSize: 11, color: '#7c3aed', fontWeight: 600, fontStyle: 'italic', marginTop: -4 },
  specsLine: { fontSize: 13, color: '#374151', fontWeight: 700 },
  specDiff: { fontSize: 11, color: '#6b7280', fontWeight: 600, marginTop: -6 },
  divider: { height: 1, background: '#f0f0f0', margin: '4px 0' },
  featureList: { display: 'flex', flexDirection: 'column', gap: 6, flexGrow: 1 },
  feature: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', fontWeight: 500 },
  featureDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  trustSignals: { fontSize: 10, color: '#9ca3af', display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', marginTop: -4 },
  trustDot: { color: '#d1d5db' },
  btn: { marginTop: 8, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'opacity 0.2s' },
  note: { textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 8, marginBottom: 0 },
  subscriptionValueMsg: { textAlign: 'center', color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 500, margin: '20px auto 0', maxWidth: 600, background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 20px', border: '1px solid rgba(255,255,255,0.15)' },
  packsSection: { marginTop: 56, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 48 },
  packsSectionHeader: { textAlign: 'center', marginBottom: 24 },
  packsSectionTitle: { color: '#fff', fontSize: 28, fontWeight: 800, margin: '0 0 10px' },
  packsSectionSub: { color: 'rgba(255,255,255,0.65)', fontSize: 15, lineHeight: 1.7, maxWidth: 680, margin: '0 auto' },
  packsCompare: { display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap', marginBottom: 32, background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 24px' },
  packsCompareItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'rgba(255,255,255,0.85)', fontWeight: 600 },
  packsCompareIcon: { fontWeight: 800, color: '#8b5cf6', fontSize: 14 },
  packsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 16 },
  packCard: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 10, borderTop: '4px solid #8b5cf6' },
  packCardName: { fontWeight: 800, fontSize: 20, color: '#1a1a1a' },
  packCardDesc: { fontSize: 13, color: '#6b7280', lineHeight: 1.5, flexGrow: 1 },
  packCardPrice: { display: 'flex', alignItems: 'baseline', gap: 2 },
  packCurrency: { fontSize: 15, fontWeight: 700, color: '#1a1a1a' },
  packUsd:      { fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.05em', alignSelf: 'flex-end', marginBottom: 4, marginLeft: 2 },
  packAmount: { fontSize: 34, fontWeight: 800, color: '#1a1a1a' },
  packDivider: { height: 1, background: '#f0f0f0', margin: '4px 0' },
  packFeatureList: { display: 'flex', flexDirection: 'column', gap: 6 },
  packFeature: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', fontWeight: 500 },
  packDot: { width: 7, height: 7, borderRadius: '50%', background: '#8b5cf6', flexShrink: 0 },
  packBtn: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), marginTop: 8, borderRadius: 10, padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'opacity 0.2s' },
};

const hpu = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  list:    { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px' },
  bold:    { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};
